import React, { useEffect, useState } from 'react';
import { Calendar } from 'lucide-react';
import { useClienteAuth } from '../../context/ClienteAuthContext';
import { supabase } from '../../lib/supabase';

type Prod = {
  id: string;
  nombre: string;
  estado: string;
  etapa: string;
  fecha_creacion: string;
  fecha_entrega_estimada: string | null;
  ficha_aprobada: string | null;
  archivo_dtf: string | null;
  archivo_bordado: string | null;
  acuerodo_servicio?: string | null;
  service_agreement_file?: string | null;
};

// Tipo para la función con fechas
type ProductionTask = {
  id: string;
  task_name: string;
  status: string;
  notes: string;
  start_date: string;
  end_date: string;
};

type FilterState = 'all' | 'pending' | 'in_progress' | 'finished';

const ClienteProducciones: React.FC = () => {
  const { cliente } = useClienteAuth();
  const [items, setItems] = useState<Prod[]>([]);
  const [filteredItems, setFilteredItems] = useState<Prod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterState>('pending');
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [productionTasks, setProductionTasks] = useState<Record<string, ProductionTask[]>>({});
  const [loadingTasks, setLoadingTasks] = useState<Set<string>>(new Set());

  useEffect(() => {
    const load = async () => {
      if (!cliente) return;
      setLoading(true);
      setError(null);
      try {
        const { data, error } = await supabase.rpc('portal_list_producciones', {
          p_portal_cliente_id: cliente.id
        });
        if (error) throw error;
        const producciones = Array.isArray(data) ? data : [];
        setItems(producciones);

    // Log activity
    try {
      await supabase.rpc('log_cliente_activity_simple', {
        p_cliente_id: cliente.id,
        p_action: 'Visualización de producciones',
        p_entity_type: 'productions',
        p_details: `Cliente visualizó ${producciones.length} producciones`,
        p_action_type: 'view'
      });
      console.log('Producciones activity logged successfully');
    } catch (logError) {
      console.error('Error logging producciones activity:', logError);
    }
      } catch (e: any) {
        console.error('Error cargando producciones:', e);
        setError('No se pudieron cargar las producciones');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [cliente]);

  useEffect(() => {
    filterItems();
  }, [items, activeFilter]);

  const filterItems = () => {
    let filtered: Prod[];
    switch (activeFilter) {
      case 'pending':
        filtered = items.filter((item: Prod) => 
          item.estado && item.estado.toLowerCase().includes('pendiente') || 
          item.estado && item.estado.toLowerCase().includes('pending')
        );
        break;
      case 'in_progress':
        filtered = items.filter((item: Prod) => 
          item.estado && item.estado.toLowerCase().includes('proceso') || 
          item.estado && item.estado.toLowerCase().includes('progress') ||
          item.estado && item.estado.toLowerCase().includes('en_progress')
        );
        break;
      case 'finished':
        filtered = items.filter((item: Prod) => 
          item.estado && item.estado.toLowerCase().includes('completado') || 
          item.estado && item.estado.toLowerCase().includes('completed') ||
          item.estado && item.estado.toLowerCase().includes('terminado') ||
          item.estado && item.estado.toLowerCase().includes('finished')
        );
        break;
      default:
        filtered = items;
    }
    setFilteredItems(filtered);
  };

  const toggleCard = (id: string) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
      // Cargar tareas cuando se expande la card
      if (!productionTasks[id]) {
        loadProductionTasks(id);
      }
    }
    setExpandedCards(newExpanded);
  };

  const loadProductionTasks = async (productionId: string) => {
    if (productionTasks[productionId]) return; // Ya cargadas
    
          setLoadingTasks((prev: Set<string>) => new Set(prev).add(productionId));
    try {
      const { data, error } = await supabase.rpc('portal_list_production_tasks_with_dates', {
        p_production_id: productionId
      });
      if (error) throw error;
      
      const tasks = Array.isArray(data) ? data : [];
      setProductionTasks((prev: Record<string, ProductionTask[]>) => ({
        ...prev,
        [productionId]: tasks
      }));
    } catch (e: any) {
      console.error('Error cargando tareas:', e);
    } finally {
      setLoadingTasks((prev: Set<string>) => {
        const newSet = new Set(prev);
        newSet.delete(productionId);
        return newSet;
      });
    }
  };

  const downloadFile = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error descargando archivo:', error);
      alert('Error al descargar el archivo');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No definida';
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'No disponible';
    return new Date(dateString).toLocaleString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Función getProgressPercentage eliminada

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completado':
      case 'completed':
      case 'terminado':
      case 'finished':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'en proceso':
      case 'en progreso':
      case 'in progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pendiente':
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelado':
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusDisplay = (status: string) => {
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes('completado') || lowerStatus.includes('completed') || 
        lowerStatus.includes('terminado') || lowerStatus.includes('finished')) {
      return 'Terminada';
    } else if (lowerStatus.includes('pendiente') || lowerStatus.includes('pending')) {
      return 'En producción';
    } else if (lowerStatus.includes('proceso') || lowerStatus.includes('progress')) {
      return 'En proceso';
    }
    return status;
  };

  const getEtapaColor = (etapa: string) => {
    switch (etapa) {
      case 'Ficha Aprobada':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'Archivo DTF':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Archivo Bordado':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'En Proceso':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getFilterCount = (filter: FilterState) => {
    switch (filter) {
      case 'pending':
        return items.filter((item: Prod) => 
          item.estado && item.estado.toLowerCase().includes('pendiente') || 
          item.estado && item.estado.toLowerCase().includes('pending')
        ).length;
      case 'in_progress':
        return items.filter((item: Prod) => 
          item.estado && item.estado.toLowerCase().includes('proceso') || 
          item.estado && item.estado.toLowerCase().includes('progress') ||
          item.estado && item.estado.toLowerCase().includes('en_progress')
        ).length;
      case 'finished':
        return items.filter((item: Prod) => 
          item.estado && item.estado.toLowerCase().includes('completado') || 
          item.estado && item.estado.toLowerCase().includes('completed') ||
          item.estado && item.estado.toLowerCase().includes('terminado') ||
          item.estado && item.estado.toLowerCase().includes('finished')
        ).length;
      default:
        return items.length;
    }
  };

    const getTaskStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'finished':
      case 'terminado':
      case 'completed':
      case 'completada':
        return 'bg-green-100 text-green-800 border-green-200'; // Verde success
      case 'in_progress':
      case 'en progreso':
      case 'in progress':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'; // Amarillo warning
      case 'pending':
      case 'pendiente':
        return 'bg-blue-100 text-blue-800 border-blue-200'; // Azul
      case 'cancelled':
      case 'cancelada':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTaskStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'finished':
      case 'terminado':
      case 'completed':
      case 'completada':
        return 'Terminado';
      case 'in_progress':
      case 'en progreso':
      case 'in progress':
        return 'En progreso';
      case 'pending':
      case 'pendiente':
        return 'Pendiente';
      case 'cancelled':
      case 'cancelada':
        return 'Cancelada';
      default:
        return status;
    }
  };

  // getPriorityColor function removed - priority column not available in database

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando producciones...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error al cargar producciones</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900 mb-2">Mis Producciones</h1>
        <p className="text-gray-600">Seguimiento detallado de todas tus producciones en curso</p>
        <div className="mt-3 sm:mt-4 flex items-center space-x-4 text-xs sm:text-sm text-gray-500">
          <span>Total: <span className="font-semibold text-gray-900">{items.length}</span> producciones</span>
          <span>•</span>
          <span>Última actualización: {new Date().toLocaleString('es-AR')}</span>
        </div>
      </div>

      {/* Filtros */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveFilter('pending')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors shadow-sm ${activeFilter === 'pending' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            En producción ({getFilterCount('pending')})
          </button>
          <button
            onClick={() => setActiveFilter('in_progress')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors shadow-sm ${activeFilter === 'in_progress' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            En proceso ({getFilterCount('in_progress')})
          </button>
          <button
            onClick={() => setActiveFilter('finished')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors shadow-sm ${activeFilter === 'finished' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            Terminadas ({getFilterCount('finished')})
          </button>
        </div>
      </div>

      {filteredItems.length === 0 ? (
        <div className="text-center py-16">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-4 text-base sm:text-lg font-semibold text-gray-900">No hay producciones</h3>
          <p className="mt-1 text-sm text-gray-500">
            {activeFilter === 'all' 
              ? 'No tienes producciones asignadas.'
              : `No hay producciones en estado "${activeFilter === 'pending' ? 'En producción' : activeFilter === 'in_progress' ? 'En proceso' : 'Terminadas'}"`}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:gap-6">
          {filteredItems.map((prod) => (
            <div key={prod.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
              {/* Header de la producción - Siempre visible */}
              <div 
                className="px-4 sm:px-6 py-4 border-b border-gray-100 bg-white cursor-pointer"
                onClick={() => toggleCard(prod.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">{prod.nombre}</h3>
                      <svg 
                        className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                          expandedCards.has(prod.id) ? 'rotate-180' : ''
                        }`}
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm mt-2 text-gray-600">
                      <span className="text-gray-500">Creada: {formatDateTime(prod.fecha_creacion)}</span>
                      {prod.fecha_entrega_estimada && (
                        <>
                          <span className="text-gray-300">•</span>
                          <span className="inline-flex items-center px-2 py-1 rounded-full border text-xs sm:text-sm bg-black text-white border-black">
                            <Calendar className="h-3.5 w-3.5 mr-1" />
                            Entrega: <span className="ml-1 font-semibold">{formatDate(prod.fecha_entrega_estimada)}</span>
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${getStatusColor(prod.estado)}`}>
                      {getStatusDisplay(prod.estado)}
                    </span>
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${getEtapaColor(prod.etapa)}`}>
                      {prod.etapa}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contenido expandible */}
              {expandedCards.has(prod.id) && (
                <>
                  {/* Contenido principal */}
                  <div className="px-6 py-4">
                    <div className="space-y-8">
                      {/* Progreso y archivos en una sola columna */}

                      {/* Progreso de Tareas de Producción */}
                      <div className="mt-8">
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Progreso</h4>

                        {loadingTasks.has(prod.id) ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span className="ml-2 text-gray-600">Cargando tareas...</span>
                          </div>
                        ) : productionTasks[prod.id] && productionTasks[prod.id].length > 0 ? (
                          <div className="space-y-6">
                            {/* Barra de progreso general */}
                            <div>
                              <div className="flex items-center justify-between mb-2 text-xs text-gray-600">
                                <span>Avance</span>
                                <span>
                                  {Math.round(
                                    (productionTasks[prod.id].filter(t => ['finished','terminado','completed','completada'].includes(String(t.status).toLowerCase())).length /
                                      Math.max(1, productionTasks[prod.id].length)) * 100
                                  )}%
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 h-2 rounded-full">
                                <div
                                  className="h-2 bg-black rounded-full transition-all"
                                  style={{
                                    width: `${Math.min(100, Math.round((productionTasks[prod.id].filter(t => ['finished','terminado','completed','completada'].includes(String(t.status).toLowerCase())).length / Math.max(1, productionTasks[prod.id].length)) * 100))}%`
                                  }}
                                />
                              </div>
                            </div>

                            {/* Versión vertical (mobile) */}
                            <div className="md:hidden relative pl-6">
                              <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-gray-200" />
                              <div className="space-y-5">
                                {productionTasks[prod.id].map((task, index) => (
                                  <div key={task.id} className="relative">
                                    <div className={`absolute -left-3 top-0 w-6 h-6 rounded-full bg-white border flex items-center justify-center ${
                                      ['finished','terminado','completed','completada'].includes(String(task.status).toLowerCase())
                                        ? 'border-green-600 text-green-600'
                                        : ['in_progress','en progreso','in progress'].includes(String(task.status).toLowerCase())
                                        ? 'border-yellow-600 text-yellow-600'
                                        : 'border-blue-600 text-blue-600'
                                    }`}>
                                      <span className="text-[10px] font-semibold">{index + 1}</span>
                                    </div>
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                      <div className="flex items-center justify-between">
                                        <h5 className="text-sm font-medium text-gray-900">{task.task_name}</h5>
                                        <span className={`px-2 py-0.5 text-[11px] rounded-full border ${getTaskStatusColor(task.status)}`}>{getTaskStatusText(task.status)}</span>
                                      </div>
                                      <div className="mt-1 grid grid-cols-2 gap-2 text-xs text-gray-600">
                                        {(task.start_date && task.start_date !== 'No disponible' && task.start_date !== 'No definida') && (
                                          <span>Inicio: {formatDate(task.start_date)}</span>
                                        )}
                                        {(task.end_date && task.end_date !== 'No disponible' && task.end_date !== 'No definida') && (
                                          <span>Fin: {formatDate(task.end_date)}</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Versión grid (desktop) */}
                            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
                              {productionTasks[prod.id].map((task, index) => (
                                <div key={task.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className={`w-8 h-8 rounded-full bg-white border flex items-center justify-center text-xs font-semibold ${
                                      ['finished','terminado','completed','completada'].includes(String(task.status).toLowerCase())
                                        ? 'border-green-600 text-green-600'
                                        : ['in_progress','en progreso','in progress'].includes(String(task.status).toLowerCase())
                                        ? 'border-yellow-600 text-yellow-600'
                                        : 'border-blue-600 text-blue-600'
                                    }`}>{index + 1}</div>
                                    <span className={`px-2 py-0.5 text-[11px] rounded-full border ${getTaskStatusColor(task.status)}`}>{getTaskStatusText(task.status)}</span>
                                  </div>
                                  <h5 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">{task.task_name}</h5>
                                  <div className="text-xs text-gray-600 space-y-1">
                                    {(task.start_date && task.start_date !== 'No disponible' && task.start_date !== 'No definida') && (
                                      <div>Inicio: {formatDate(task.start_date)}</div>
                                    )}
                                    {(task.end_date && task.end_date !== 'No disponible' && task.end_date !== 'No definida') && (
                                      <div>Fin: {formatDate(task.end_date)}</div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <svg className="mx-auto h-12 w-12 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            <p className="text-sm">No hay tareas de producción asignadas</p>
                          </div>
                        )}
                      </div>

                      {/* Archivos de producción */}
                      <div className="mt-8">
                        <h4 className="text-lg font-medium text-gray-900 mb-4">Archivos de producción</h4>
                      <div className="space-y-3">
                        {/* Solo mostrar archivos que existen */}
                        {prod.ficha_aprobada && (
                          <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <p className="text-sm font-medium text-emerald-900">Ficha Aprobada</p>
                                <p className="text-xs text-emerald-700">Documento aprobado y listo</p>
                              </div>
                            </div>
                            <button
                              onClick={() => downloadFile(prod.ficha_aprobada!, `ficha_aprobada_${prod.nombre}.pdf`)}
                              className="px-3 py-1 text-xs font-medium text-emerald-700 bg-emerald-100 rounded-md hover:bg-emerald-200 transition-colors"
                            >
                              Descargar
                            </button>
                          </div>
                        )}

                        {prod.archivo_dtf && (
                          <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <p className="text-sm font-medium text-blue-900">Archivo DTF</p>
                                <p className="text-xs text-blue-700">Archivo de transferencia digital</p>
                              </div>
                            </div>
                            <button
                              onClick={() => downloadFile(prod.archivo_dtf!, `archivo_dtf_${prod.nombre}.pdf`)}
                              className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 transition-colors"
                            >
                              Descargar
                            </button>
                          </div>
                        )}

                        {prod.archivo_bordado && (
                          <div className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <p className="text-sm font-medium text-purple-900">Archivo Bordado</p>
                                <p className="text-xs text-purple-700">Archivo de bordado disponible</p>
                              </div>
                            </div>
                            <button
                              onClick={() => downloadFile(prod.archivo_bordado!, `archivo_bordado_${prod.nombre}.pdf`)}
                              className="px-3 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded-md hover:bg-purple-200 transition-colors"
                            >
                              Descargar
                            </button>
                          </div>
                        )}

                        {(prod.acuerodo_servicio || prod.service_agreement_file) && (
                          <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <p className="text-sm font-medium text-amber-900">Acuerdo de Servicio</p>
                                <p className="text-xs text-amber-700">Contrato firmado</p>
                              </div>
                            </div>
                            <button
                              onClick={() => downloadFile(prod.acuerodo_servicio || prod.service_agreement_file!, `acuerdo_servicio_${prod.nombre}.pdf`)}
                              className="px-3 py-1 text-xs font-medium text-amber-700 bg-amber-100 rounded-md hover:bg-amber-200 transition-colors"
                            >
                              Descargar
                            </button>
                          </div>
                        )}

                        {/* Mostrar mensaje si no hay archivos */}
                        {!prod.ficha_aprobada && !prod.archivo_dtf && !prod.archivo_bordado && !(prod.acuerodo_servicio || prod.service_agreement_file) && (
                          <div className="text-center py-6 text-gray-500">
                            <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p className="text-sm">No hay archivos disponibles aún</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  </div>

                  {/* Footer con información adicional */}
                  <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>ID: {prod.id}</span>
                      <span>Última actualización: {formatDateTime(prod.fecha_creacion)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClienteProducciones;